import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Smartphone, RefreshCcw, Wifi, WifiOff, Loader2, AlertTriangle, Building2, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../components/Confirm";

export default function WhatsAppConnect() {
  const confirm = useConfirm();
  const nav = useNavigate();
  const [qr, setQr] = useState(null);
  const [status, setStatus] = useState("disconnected"); // disconnected|qr|connecting|connected|bridge_offline
  const [provider, setProvider] = useState("baileys"); // baileys|jalpi
  const [jalpiConnected, setJalpiConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [ackRisk, setAckRisk] = useState(false);
  const [health, setHealth] = useState(null);
  const [savingMode, setSavingMode] = useState(false);

  const loadHealth = () => api.get("/whatsapp/health").then((r) => setHealth(r.data)).catch(() => {});
  useEffect(() => { loadHealth(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const setAutoMode = async (on) => {
    setSavingMode(true);
    try {
      await api.post("/settings/user", { waAutoMode: on });
      setHealth((h) => (h ? { ...h, auto_mode: on, mode: on ? (h.schedule?.enabled ? "schedule" : "always_on") : "presence" } : h));
      toast.success(on ? "Auto mode on — Brainydo stays connected" : "Auto mode off — connects only while you use Brainydo");
    } catch {
      toast.error("Couldn't change the mode");
    } finally {
      setSavingMode(false);
    }
  };
  const pollRef = useRef(null);
  const mountPollRef = useRef(null);
  const prevStatusRef = useRef(null);

  const refreshStatus = async () => {
    try {
      const s = await api.get("/whatsapp/status");
      const newStatus = s.data.status || (s.data.connected ? "connected" : "disconnected");
      if (s.data.provider) setProvider(s.data.provider);
      setJalpiConnected(!!s.data.connected);

      // Detect any transition into "connected" — covers manual QR scan, manual
      // generate, and the auto-reconnect that fires after login (disconnected→connected).
      // Initial mount when already connected uses prevStatusRef.current === null, so
      // no spurious toast on page load.
      if (
        (prevStatusRef.current === "connecting" ||
          prevStatusRef.current === "qr" ||
          prevStatusRef.current === "disconnected") &&
        newStatus === "connected"
      ) {
        toast.success("WhatsApp connected! Brainydo is now reading your messages.");
      }

      // Detect: was connecting/qr, now disconnected = session rejected (device removed)
      if (
        (prevStatusRef.current === "connecting" || prevStatusRef.current === "qr") &&
        newStatus === "disconnected" &&
        !s.data.qr_image
      ) {
        setGenerateError("Your WhatsApp session has expired — the device was likely removed from the phone. Generate a new QR code to reconnect.");
        setQr(null);
      }

      prevStatusRef.current = newStatus;
      setStatus(newStatus);
      if (s.data.qr_image) setQr(s.data.qr_image);
      if (s.data.connected) setQr(null);
    } catch {}
  };

  useEffect(() => {
    (async () => { await refreshStatus(); setLoading(false); })();
    return () => clearInterval(pollRef.current);
  }, []);

  // Initial polling window — catches auto-reconnect from login, which fires
  // /whatsapp/connect in the background and takes a few seconds to complete.
  // Without this, the page shows "Not connected" until manual refresh.
  // Only relevant to the Baileys flow (QR pairing reconnect); Jalpi accounts
  // don't need this, so stop as soon as a poll tells us the provider is jalpi.
  useEffect(() => {
    let ticks = 0;
    const maxTicks = 15; // ~30s at 2s intervals
    mountPollRef.current = setInterval(async () => {
      await refreshStatus();
      ticks += 1;
      if (ticks >= maxTicks) clearInterval(mountPollRef.current);
    }, 2000);
    return () => clearInterval(mountPollRef.current);
  }, []);

  // Stop the mount-polling window early once we know the account is on Jalpi.
  useEffect(() => {
    if (provider === "jalpi") clearInterval(mountPollRef.current);
  }, [provider]);

  // Poll while waiting for QR scan; stop when connected or disconnected
  useEffect(() => {
    clearInterval(pollRef.current);
    if (status === "qr" || status === "connecting") {
      pollRef.current = setInterval(refreshStatus, 2500);
    }
    return () => clearInterval(pollRef.current);
  }, [status]);

  const generateQR = async () => {
    setGenerating(true);
    setGenerateError(null);
    setQr(null);
    prevStatusRef.current = null;
    try {
      // Always wipe any existing session/auth before requesting a new QR.
      await api.post("/whatsapp/disconnect").catch(() => {});
      const r = await api.post("/whatsapp/connect");
      setQr(r.data.qr_image || null);
      setStatus(r.data.status || "qr");
      if (r.data.status === "connected") {
        toast.success("Already connected");
        await refreshStatus();
      } else if (!r.data.qr_image) {
        toast.info("QR code is loading — it should appear within a few seconds.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start pairing";
      const isOffline = msg.toLowerCase().includes("offline");
      setGenerateError(isOffline
        ? "WhatsApp bridge is offline. Contact support."
        : msg
      );
      if (isOffline) setStatus("bridge_offline");
    } finally { setGenerating(false); }
  };

  const disconnect = async () => {
    const ok = await confirm({
      title: "Disconnect WhatsApp?",
      message: "You'll need to scan a new QR to re-pair this device.",
      confirmLabel: "Disconnect",
      tone: "danger",
    });
    if (!ok) return;
    await api.post("/whatsapp/disconnect");
    toast.success("Disconnected");
    setStatus("disconnected");
    setQr(null);
    prevStatusRef.current = null;
  };

  const connected = status === "connected";

  return (
    <div className="p-6 md:p-8 max-w-[1100px] mx-auto" data-testid="wa-page">
      {health?.link_lost_at && status !== "connected" && (
        <div className="mb-5 flex items-start gap-3 p-4 rounded-xl border border-red-200 bg-red-50" data-testid="wa-link-lost-note">
          <WifiOff size={18} className="text-red-600 shrink-0 mt-0.5" />
          <div className="text-[13px] text-red-900">
            <div className="font-bold">WhatsApp removed Brainydo as a linked device</div>
            <div className="text-red-700 mt-0.5">Since {new Date(health.link_lost_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })} no messages have been read and no tasks created. Generate a new QR code below and scan it from WhatsApp → Linked devices. Everything resumes automatically once linked.</div>
          </div>
        </div>
      )}
      <div className="mb-6">
        <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900 leading-tight">WhatsApp Connect</h1>
        <p className="text-[14px] text-slate-500 mt-1">
          Pair your WhatsApp Business by scanning a QR code. Brainydo will start monitoring your messages immediately.
        </p>
        {status === "bridge_offline" && (
          <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 border border-red-200 bg-red-50 rounded-xl" data-testid="bridge-offline-banner">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-red-700">Bridge offline</span>
            <span className="text-[12.5px] text-red-800">
              The WhatsApp bridge service is not running. Contact support.
            </span>
          </div>
        )}
      </div>

      {provider === "jalpi" ? (
        <section className="bg-white border border-slate-200 rounded-xl p-8" data-testid="wa-jalpi-section">
          {jalpiConnected ? (
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 grid place-items-center rounded-xl bg-emerald-50 border border-emerald-200">
                <Wifi size={22} className="text-emerald-600" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 mb-0.5">Connected</div>
                <div className="text-[16px] font-bold text-slate-900">Connected via WhatsApp Business API (Jalpi)</div>
                <div className="text-[13px] text-slate-500 mt-0.5">Messages are being processed automatically.</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 grid place-items-center rounded-xl bg-amber-50 border border-amber-200">
                <Building2 size={22} className="text-amber-600" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700 mb-0.5">Not connected</div>
                <div className="text-[16px] font-bold text-slate-900">Jalpi selected but no API key</div>
                <div className="text-[13px] text-slate-500 mt-0.5">
                  Configure it in{" "}
                  <Link to="/app/settings/channel" className="text-blue-600 underline">
                    Settings → WhatsApp Channel
                  </Link>
                  .
                </div>
              </div>
            </div>
          )}
        </section>
      ) : !connected ? (
        <section className="bg-white border border-slate-200 rounded-xl p-8 grid md:grid-cols-[260px_1fr] gap-8 items-center" data-testid="wa-connect-section">
          {/* QR panel */}
          <div className="aspect-square w-full max-w-[260px] border border-slate-200 rounded-xl bg-slate-50 grid place-items-center overflow-hidden relative">
            {qr ? (
              <img src={qr} alt="QR code" className="w-full h-full object-contain p-2" data-testid="wa-qr-img" />
            ) : generating ? (
              <div className="text-center px-4">
                <Loader2 className="mx-auto text-slate-500 animate-spin" size={28} />
                <div className="text-[12.5px] text-slate-500 mt-2">Starting up…</div>
              </div>
            ) : status === "connecting" || status === "qr" ? (
              <div className="text-center px-4">
                <Loader2 className="mx-auto text-slate-500 animate-spin" size={28} />
                <div className="text-[12.5px] text-slate-500 mt-2">Connecting to WhatsApp…</div>
                <div className="text-[11px] text-slate-400 mt-1">QR code will appear here shortly</div>
              </div>
            ) : generateError ? (
              <div className="text-center px-5">
                <AlertTriangle className="mx-auto text-amber-500 mb-2" size={28} />
                <div className="text-[12px] text-slate-700 font-medium mb-1">Failed to generate QR</div>
                <div className="text-[11px] text-slate-500 mb-3">{generateError}</div>
                <button
                  onClick={generateQR}
                  disabled={!ackRisk}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[12px] font-semibold transition disabled:opacity-50"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="text-center px-4">
                <Smartphone className="mx-auto text-slate-300" size={36} />
                <div className="text-[12.5px] text-slate-500 mt-2">Generate a QR code to begin</div>
              </div>
            )}
            {status === "qr" && qr && (
              <div className="absolute bottom-2 left-2 right-2 text-center">
                <span className="inline-flex items-center gap-1.5 text-[10.5px] px-2 py-1 bg-white/95 border border-slate-200 rounded-lg font-medium">
                  <Loader2 size={11} className="animate-spin" /> Waiting for scan
                </span>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div>
            <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-500 mb-1">
              <WifiOff size={13} /> Not connected
            </div>
            <h2 className="text-[22px] font-bold text-slate-900">Pair your device</h2>
            <ol className="mt-4 space-y-2.5 text-[14px] text-slate-700 list-decimal list-inside">
              <li>Open <span className="font-semibold">WhatsApp</span> on your phone.</li>
              <li>Tap <span className="font-semibold">Settings → Linked devices → Link a device</span>.</li>
              <li>Scan the QR code shown here. The page updates automatically once paired.</li>
            </ol>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                onClick={generateQR}
                disabled={!ackRisk || generating || loading || status === "bridge_offline"}
                className="h-10 px-5 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[14px] font-semibold transition active:scale-95 disabled:opacity-60"
                data-testid="wa-generate-qr-btn"
              >
                <RefreshCcw size={14} />
                {generating ? "Starting…" : qr ? "Regenerate QR" : "Generate QR code"}
              </button>
            </div>
            <div className="mt-5 p-4 border border-amber-300 bg-amber-50 rounded-xl text-[12.5px] text-amber-900 space-y-2">
              <div className="font-bold flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[15px]" style={{ fontVariationSettings: "'FILL' 1" }}>warning</span>
                Important: WhatsApp Terms of Service
              </div>
              <p>
                Brainydo connects using <strong>WhatsApp Web (Baileys)</strong>, an unofficial method that is <strong>not authorised by WhatsApp/Meta</strong>. Using this feature violates WhatsApp's Terms of Service and your account may be temporarily or permanently banned.
              </p>
              <p>
                <strong>Recommendation:</strong> Use a dedicated WhatsApp Business number for this tool — not your personal number. If banned, that number will lose access to all its chats and contacts.
              </p>
              <label className="flex items-start gap-2 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={ackRisk}
                  onChange={(e) => setAckRisk(e.target.checked)}
                  className="mt-0.5 accent-amber-600"
                />
                <span>I understand the risks and accept full responsibility for my WhatsApp account.</span>
              </label>
            </div>
          </div>
        </section>
      ) : (
        <section data-testid="wa-connected-section" className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 grid place-items-center rounded-xl bg-emerald-50 border border-emerald-200">
                <Wifi size={22} className="text-emerald-600" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 mb-0.5">Connected</div>
                <div className="text-[16px] font-bold text-slate-900">Brainydo is reading your WhatsApp</div>
                <div className="text-[13px] text-slate-500 mt-0.5">Messages are being processed automatically.</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => nav("/app/dashboard")}
                className="h-10 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold transition"
                data-testid="wa-go-dashboard"
              >
                Go to Dashboard
              </button>
              <button
                onClick={disconnect}
                className="h-10 px-4 border border-slate-200 hover:border-red-400 hover:text-red-600 rounded-xl text-[13px] transition"
                data-testid="wa-disconnect-btn"
              >
                Disconnect
              </button>
            </div>
          </div>

          {/* Auto mode */}
          <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="wa-auto-mode">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-200 grid place-items-center shrink-0">
                  <ShieldCheck size={17} className="text-blue-600" />
                </div>
                <div>
                  <div className="text-[14px] font-bold text-slate-900">Auto mode</div>
                  <p className="text-[12.5px] text-slate-500 mt-0.5 max-w-xl">
                    Keep Brainydo connected around the clock, even when you're logged out, so messages become tasks, reminders go out and approvals work without anyone opening the app. Your number is only used to read chats; if the link ever drops, Brainydo reconnects on its own and emails you if it can't.
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                <input type="checkbox" className="sr-only peer" checked={health ? health.auto_mode !== false : true} disabled={savingMode || !health} onChange={(e) => setAutoMode(e.target.checked)} data-testid="wa-auto-mode-toggle" />
                <div className="w-11 h-6 bg-slate-200 peer-checked:bg-emerald-600 rounded-full peer peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-disabled:opacity-50" />
              </label>
            </div>
            {health && (
              <div className="mt-4 grid sm:grid-cols-3 gap-3 text-[12.5px]">
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">Mode</div>
                  <div className="font-semibold text-slate-800 mt-0.5 inline-flex items-center gap-1.5">
                    {health.mode === "schedule" ? <><Clock size={12} /> On a schedule</> : health.mode === "always_on" ? "Connected 24/7" : "While you use Brainydo"}
                  </div>
                  {health.mode === "schedule" && (
                    <div className="text-slate-500 text-[11.5px] mt-0.5">
                      {health.schedule_open ? "Inside the window now" : "Paused by schedule"}{health.next_change ? ` · changes ${new Date(health.next_change).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" })}` : ""}
                    </div>
                  )}
                  <Link to="/app/settings" className="text-[11.5px] text-blue-600 underline-offset-2 hover:underline">Connection schedule</Link>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">Connected since</div>
                  <div className="font-semibold text-slate-800 mt-0.5">{health.connected_since ? new Date(health.connected_since).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—"}</div>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">Activity</div>
                  <div className="text-slate-800 mt-0.5">Last message read: <span className="font-semibold">{health.last_message_at ? new Date(health.last_message_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—"}</span></div>
                  <div className="text-slate-800">Last notification: <span className="font-semibold">{health.last_notification_at ? new Date(health.last_notification_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—"}</span></div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
